// A scene, drawn for the dashboard.
//
// Mirrors ScenePitch on mobile: same coordinate space, same chalk, same
// rule that frames carry only what changes. It does NOT animate — an
// author is checking that frame 3 shows the right picture, and stepping
// to it through half a second of easing is slower, not clearer. The app
// animates because a learner is being shown movement; the preview does
// not because an author is being shown states.
import { useState } from "react";
import { CHALK, PITCH_COLOR as C } from "./geometry";

type Pos = { x: number; y: number };

export type ScenePlayer = {
  id: string;
  team: "blue" | "red";
  label: string;
  x: number;
  y: number;
};

export type SceneFrame = {
  caption?: string;
  moves?: Record<string, Pos>;
  ball?: { at?: string; x?: number; y?: number };
  lines?: {
    y?: number;
    throughPlayer?: string;
    label?: string;
    tone?: "neutral" | "warn" | "good";
  }[];
  highlight?: string[];
};

export type ScenePayload = {
  players?: ScenePlayer[];
  frames?: SceneFrame[];
  mode?: "pick_player" | "choice";
  text?: string;
  options?: { id: string; text: string }[];
  correctPlayerIds?: string[];
  correctOptionId?: string;
  explanation?: string;
};

// Same computation as the app's sceneStateAt, deliberately. If these two
// ever disagree the preview is lying, which is worse than not having one.
export function stateAt(payload: ScenePayload, index: number) {
  const positions = new Map<string, Pos>();
  for (const p of payload.players ?? []) positions.set(p.id, { x: p.x, y: p.y });

  let ball: SceneFrame["ball"];
  let lines: NonNullable<SceneFrame["lines"]> = [];
  let highlight: string[] = [];

  const frames = payload.frames ?? [];
  for (let i = 0; i <= index && i < frames.length; i++) {
    const f = frames[i];
    for (const [id, pos] of Object.entries(f.moves ?? {})) positions.set(id, pos);
    if (f.ball) ball = f.ball;
    if (f.lines) lines = f.lines;
    highlight = f.highlight ?? [];
  }
  return { positions, ball, lines, highlight };
}

export default function ScenePreview({
  payload,
  width,
  /** Questions show one fixed moment — the one being asked about. */
  fixedToLastFrame = false,
}: {
  payload: ScenePayload;
  width: number;
  fixedToLastFrame?: boolean;
}) {
  const frames = payload.frames ?? [];
  const [index, setIndex] = useState(0);
  const shown = fixedToLastFrame ? Math.max(0, frames.length - 1) : index;
  const state = stateAt(payload, shown);

  const token = Math.max(18, (30 * width) / 330);
  const correct = new Set(payload.correctPlayerIds ?? []);

  if (!payload.players?.length || !frames.length) {
    return (
      <p className="rounded border border-dashed p-6 text-center text-xs text-muted-foreground">
        A scene needs at least one player and one frame.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div
        className="relative overflow-hidden rounded-[14px] border"
        style={{
          width,
          height: width * 1.5,
          background: C.boardPitchBg,
          borderColor: "rgba(255,255,255,0.08)",
        }}
      >
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox={CHALK.viewBox}
          preserveAspectRatio="none"
        >
          <rect {...rect(CHALK.outer)} stroke={C.chalk} strokeWidth={0.6} fill="none" />
          {CHALK.goals.map((g, i) => (
            <rect
              key={`goal-${i}`}
              {...rect(g)}
              stroke="rgba(255,255,255,0.85)"
              strokeWidth={0.9}
              fill="rgba(255,255,255,0.10)"
            />
          ))}
          <line
            x1={CHALK.outer.x}
            y1={CHALK.halfway.y}
            x2={CHALK.outer.x + CHALK.outer.w}
            y2={CHALK.halfway.y}
            stroke={C.chalk}
            strokeWidth={0.6}
          />
          <circle
            cx={CHALK.centreCircle.cx}
            cy={CHALK.centreCircle.cy}
            r={CHALK.centreCircle.r}
            stroke={C.chalk}
            strokeWidth={0.6}
            fill="none"
          />
          {CHALK.boxes.map((b, i) => (
            <rect key={i} {...rect(b)} stroke={C.chalk} strokeWidth={0.6} fill="none" />
          ))}
        </svg>

        {/* Lines under the tokens — a line through the last defender must
            not cover the defender it is drawn through. */}
        {state.lines.map((line, i) => {
          const y =
            line.throughPlayer != null
              ? state.positions.get(line.throughPlayer)?.y
              : line.y;
          if (typeof y !== "number") return null;
          const colour =
            line.tone === "warn"
              ? C.gold
              : line.tone === "good"
                ? C.correct
                : "rgba(255,255,255,0.55)";
          return (
            <div
              key={i}
              className="pointer-events-none absolute left-0 right-0"
              style={{ top: `${y}%` }}
            >
              <div style={{ height: 2, background: colour }} />
              {line.label && (
                <span
                  className="absolute right-1.5 mt-0.5 rounded px-1 py-0.5 text-[9px] font-bold"
                  style={{ background: "rgba(8,20,14,0.85)", color: colour }}
                >
                  {line.label}
                </span>
              )}
            </div>
          );
        })}

        {payload.players.map((p) => {
          const pos = state.positions.get(p.id) ?? { x: p.x, y: p.y };
          const lit = state.highlight.includes(p.id);
          const isAnswer = correct.has(p.id);
          const base = p.team === "blue" ? C.blue : C.red;
          return (
            <div
              key={p.id}
              title={`${p.id} (${p.team})`}
              className="absolute flex items-center justify-center rounded-full font-extrabold"
              style={{
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                width: token,
                height: token,
                marginLeft: -token / 2,
                marginTop: -token / 2,
                // A ring, not a repaint — see ScenePitch for why team
                // colour is the one thing in a scene that must not move.
                background: isAnswer ? C.correct : base,
                color: isAnswer ? "#10240F" : p.team === "blue" ? C.blueInk : C.redInk,
                fontSize: Math.max(8, token * 0.36),
                boxShadow: lit ? "0 0 9px rgba(255,215,0,0.9)" : undefined,
                border:
                  lit && !isAnswer
                    ? `3px solid ${C.gold}`
                    : isAnswer
                      ? `2px solid ${C.correct}`
                      : undefined,
              }}
            >
              {p.label}
            </div>
          );
        })}

        {state.ball && <Ball ball={state.ball} positions={state.positions} />}
      </div>

      {!fixedToLastFrame && frames.length > 1 && (
        <div className="flex flex-wrap items-center gap-1">
          {frames.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              className={
                "h-6 w-6 cursor-pointer rounded text-[11px] font-bold transition-colors " +
                (i === shown
                  ? "bg-emerald-600 text-white"
                  : "bg-muted text-muted-foreground hover:bg-muted/70")
              }
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {frames[shown]?.caption && (
        <p className="text-xs" style={{ color: C.inkDim }}>
          {frames[shown].caption}
        </p>
      )}
    </div>
  );
}

function Ball({
  ball,
  positions,
}: {
  ball: NonNullable<SceneFrame["ball"]>;
  positions: Map<string, Pos>;
}) {
  const carried = ball.at ? positions.get(ball.at) : undefined;
  const x = carried ? carried.x + 3.5 : (ball.x ?? 50);
  const y = carried ? carried.y + 3 : (ball.y ?? 50);
  return (
    <svg
      className="pointer-events-none absolute"
      width={17}
      height={17}
      viewBox="0 0 24 24"
      style={{ left: `${x}%`, top: `${y}%`, marginLeft: -8.5, marginTop: -8.5 }}
    >
      <circle cx={12} cy={12} r={11} fill="#fff" stroke="#0B1F14" strokeWidth={1.2} />
      <polygon points="12,7.4 16.38,10.58 14.71,15.72 9.29,15.72 7.62,10.58" fill="#0B1F14" />
      <line x1={12} y1={7.4} x2={12} y2={1.4} stroke="#0B1F14" strokeWidth={1.5} />
      <line x1={16.38} y1={10.58} x2={22.46} y2={8.6} stroke="#0B1F14" strokeWidth={1.5} />
      <line x1={14.71} y1={15.72} x2={18.47} y2={20.9} stroke="#0B1F14" strokeWidth={1.5} />
      <line x1={9.29} y1={15.72} x2={5.53} y2={20.9} stroke="#0B1F14" strokeWidth={1.5} />
      <line x1={7.62} y1={10.58} x2={1.54} y2={8.6} stroke="#0B1F14" strokeWidth={1.5} />
    </svg>
  );
}

function rect(b: { x: number; y: number; w: number; h: number }) {
  return { x: b.x, y: b.y, width: b.w, height: b.h };
}
